/**
 * @package  	Cavalree_Infatree_Theme
 * @copyright	Copyright (c) 2021 Infantree (https://infantree.com/)
 * @version  	1.0.0
 *
 */

if (typeof $ === "undefined") {
  var $ = jQuery;
}

/*****************************
 * Vanilla JS functions
 *****************************/
// addHeadingNonBreakingSpace();
// detectScrollbarWidth();

function enhance(root) {
  var nodes = root.querySelectorAll(
    "span.is-style-cav-tooltip:not([data-cav-tooltipped])",
  );
  nodes.forEach(function (el) {
    el.setAttribute("data-cav-tooltipped", "1");

    // If it already has the structure, skip
    if (el.querySelector(".cav-tooltip__content")) return;

    // Create icon
    var icon = document.createElement("i");
    icon.className = "icon-info";

    // Create content wrapper and move *all* existing child nodes into it
    var content = document.createElement("span");
    content.className = "cav-tooltip__content";
    while (el.firstChild) {
      content.appendChild(el.firstChild); // preserves inner HTML (links, strong, etc.)
    }

    // Rebuild: <i> + <span.cav-tooltip__content>...</span>
    el.appendChild(icon);
    el.appendChild(content);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", function () {
    enhance(document);
    updateHeaderBackground();
  });
} else {
  enhance(document);
  updateHeaderBackground();
}

// Print style accordions.
document.addEventListener("DOMContentLoaded", () => {
  const DETAILS_SELECTOR = "details";

  // Snapshot of state before printing
  let snapshot = null;

  function takeSnapshot(details) {
    return details.map((el) => ({
      el,
      wasOpen: el.open,
      name: el.getAttribute("name"), // null if absent
      hadName: el.hasAttribute("name"),
    }));
  }

  function openAllForPrint() {
    const details = Array.from(document.querySelectorAll(DETAILS_SELECTOR));
    snapshot = takeSnapshot(details);

    // If name-based accordion grouping is in play, remove name temporarily
    // so multiple can be open at once.
    details.forEach((d) => {
      if (d.hasAttribute("name")) d.removeAttribute("name");
    });

    // Now open everything
    details.forEach((d) => {
      d.open = true;
    });
  }

  function restoreAfterPrint() {
    if (!snapshot) return;

    // Restore name + open state exactly as it was
    snapshot.forEach(({ el, wasOpen, hadName, name }) => {
      if (hadName) el.setAttribute("name", name ?? "");
      else el.removeAttribute("name");

      el.open = wasOpen;
    });

    snapshot = null;
  }

  window.addEventListener("beforeprint", openAllForPrint);
  window.addEventListener("afterprint", restoreAfterPrint);

  // Fallback: some browsers are flaky with afterprint
  window.addEventListener("focus", restoreAfterPrint);

  const openSearch = document.querySelector(
    "button.site-header__eyebrow--search",
  );
  const searchModal = document.querySelector(".search-modal");
  const searchClose = searchModal.querySelector(".search-modal__close");
  openSearch.addEventListener("click", () => {
    searchModal.classList.add("is-active");
  });

  searchClose.addEventListener("click", () => {
    searchModal.classList.remove("is-active");
  });
});

// Optional: if you trigger printing via JS, you can wrap it:
// window.print() will still trigger beforeprint/afterprint where supported.

const OFFSET = 8;
const PADDING = 8;

function place(panel, trigger) {
  // Clear previous placement styles for accurate measuring
  panel.style.left = panel.style.top = "";
  panel.style.right = panel.style.bottom = "";
  panel.style.transform = "";
  panel.removeAttribute("data-placement");

  const tRect = trigger.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  // Temporarily ensure measurable box under your CSS cap
  const prevDisplay = panel.style.display;
  const prevVis = panel.style.visibility;
  panel.style.display = "block";
  panel.style.visibility = "hidden";

  const pRect = panel.getBoundingClientRect();

  const roomTop = tRect.top - PADDING - OFFSET;
  const roomBottom = vh - tRect.bottom - PADDING - OFFSET;
  const roomLeft = tRect.left - PADDING - OFFSET;
  const roomRight = vw - tRect.right - PADDING - OFFSET;

  const candidates = [
    { dir: "bottom", fits: roomBottom >= pRect.height, space: roomBottom },
    { dir: "top", fits: roomTop >= pRect.height, space: roomTop },
    { dir: "right", fits: roomRight >= pRect.width, space: roomRight },
    { dir: "left", fits: roomLeft >= pRect.width, space: roomLeft },
  ];
  let chosen =
    candidates.find((c) => c.fits)?.dir ||
    candidates.reduce((a, b) => (a.space > b.space ? a : b)).dir;

  let left, top;
  if (chosen === "bottom" || chosen === "top") {
    left = tRect.left + tRect.width / 2 - pRect.width / 2;
    top =
      chosen === "bottom"
        ? tRect.bottom + OFFSET
        : tRect.top - pRect.height - OFFSET;
  } else {
    left =
      chosen === "right"
        ? tRect.right + OFFSET
        : tRect.left - pRect.width - OFFSET;
    top = tRect.top + tRect.height / 2 - pRect.height / 2;
  }

  // Clamp within viewport
  left = Math.min(Math.max(left, PADDING), vw - PADDING - pRect.width);
  top = Math.min(Math.max(top, PADDING), vh - PADDING - pRect.height);

  panel.style.left = `${left}px`;
  panel.style.top = `${top}px`;
  panel.setAttribute("data-placement", chosen);

  panel.style.visibility = prevVis;
  panel.style.display = prevDisplay;
}

function hasTransformedAncestor(el) {
  let n = el.parentElement;
  while (n && n !== document.body) {
    const cs = getComputedStyle(n);
    if (cs.transform && cs.transform !== "none") return true;
    if (cs.willChange && cs.willChange.includes("transform")) return true;
    n = n.parentElement;
  }
  return false;
}

function bindTooltip(el) {
  if (el.dataset.cavPlacementBound) return;
  el.dataset.cavPlacementBound = "1";

  const panel = el.querySelector(".cav-tooltip__content");
  if (!panel) return;

  // Portaling bookkeeping
  let portal = false;
  let prevParent = null;
  let prevNext = null;

  const portalToBody = () => {
    if (portal) return;
    prevParent = panel.parentNode;
    prevNext = panel.nextSibling;
    document.body.appendChild(panel);
    portal = true;
  };
  const restoreFromPortal = () => {
    if (!portal) return;
    if (prevParent) {
      if (prevNext) prevParent.insertBefore(panel, prevNext);
      else prevParent.appendChild(panel);
    }
    portal = false;
  };

  const show = () => {
    panel.hidden = false;
    panel.style.visibility = "hidden";
    panel.style.opacity = "0";
    panel.style.display = "block";

    if (hasTransformedAncestor(el)) portalToBody();

    requestAnimationFrame(() => {
      place(panel, el);
      panel.style.visibility = "";
      panel.style.opacity = "";
    });
  };
  const hide = () => {
    panel.hidden = true;
    panel.style.display = "";
    panel.removeAttribute("data-placement");
    restoreFromPortal();
  };

  el.addEventListener("mouseenter", show);
  el.addEventListener("focusin", show);
  el.addEventListener("mouseleave", hide);
  el.addEventListener("focusout", hide);

  const onReflow = () => {
    if (!panel.hidden) place(panel, el);
  };
  window.addEventListener("resize", onReflow);
  window.addEventListener("scroll", onReflow, true);

  const ro = new ResizeObserver(onReflow);
  ro.observe(panel);

  panel.hidden = true;
}

function init(root) {
  root.querySelectorAll(".is-style-cav-tooltip").forEach(bindTooltip);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => init(document));
} else {
  init(document);
}

document.addEventListener("cav:wasteResultsLoaded", (e) => {
  init(document);
});

function updateHeaderBackground() {
  const siteHeader = document.querySelector(".site-header");
  const isBelowBreakpoint = window.innerWidth < 1100;
  const isScrolled = window.scrollY > 50;

  if (isBelowBreakpoint && isScrolled) {
    siteHeader.style.backgroundColor = "var(--wp--preset--color--charcoal)";
  } else {
    siteHeader.style.backgroundColor = "";
  }
}

window.addEventListener("resize", updateHeaderBackground);
window.addEventListener("scroll", updateHeaderBackground);
// END Vanilla JS

/*****************************
 * jQuery Ready
 *****************************/
$(document).ready(function () {});

// END jQuery Ready
function detectScrollbarWidth() {
  /* Detect scrollbar width to properly center full- and wide-width elements */
  let root = document.documentElement;
  var outside = document.createElement("div");
  var inside = document.createElement("div");

  outside.style.width = inside.style.width = "100%";
  outside.style.overflow = "scroll";
  document.body.appendChild(outside).appendChild(inside);

  var scrollbarWidth = outside.offsetWidth - inside.offsetWidth;

  outside.parentNode.removeChild(outside);
  root.style.setProperty("--scrollbar-width", scrollbarWidth + "px");
}

function addHeadingNonBreakingSpace() {
  var headings = document.querySelectorAll("h1, h2, h3, h4, h5, h6");

  headings.forEach(function (head, index) {
    let str = head.textContent;
    let firstSection = str.substring(0, str.lastIndexOf(" ") + 1);
    let lastSection = str.substring(str.lastIndexOf(" ") + 1, str.length);

    firstSection = firstSection.trim();

    let newStr = firstSection + "\u00a0" + lastSection;

    head.innerHTML = newStr;
  });
}
